import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { ApiError } from '@/types/carAuctionClient';

// Email Notification Settings
export interface EmailNotificationSettings {
  marketing: boolean;
  auctionUpdatesOwn: boolean;
  auctionUpdatesBidded: boolean;
  vehicleTestUpdates: boolean;
  news: boolean;
  /** E-post vid status-/prioritetsändringar på kundresor (endast bilhandlare). */
  journeyUpdates?: boolean;
}

/** Push prefs — independent from email. Native app only. */
export interface PushNotificationSettings {
  auctionUpdatesOwn: boolean;
  auctionUpdatesBidded: boolean;
  vehicleTestUpdates: boolean;
}

// Auction Settings
export interface AuctionSettings {
  automaticBidding: boolean;
  showEndingSoon: boolean;
  hideFinishedAuctions: boolean;
  showReservePrice: boolean;
  standardBidIncrement: number | null;
  preferredAuctionType: string | null;
  notifyAtBidLimit: number | null;
}

// Payment Information
export interface PaymentInformation {
  id: string;
  paymentMethod?: string; // "bank", "bankgiro", "iban"
  bankName: string;
  accountNumber: string;
  clearingNumber?: string;
  accountHolder?: string;
  isDefault: boolean;
}

export interface CreatePaymentInformationRequest {
  paymentMethod?: string;
  bankName: string;
  accountNumber: string;
  clearingNumber?: string;
  accountHolder?: string;
  isDefault?: boolean;
}

export interface UpdatePaymentInformationRequest {
  paymentMethod?: string;
  bankName?: string;
  accountNumber?: string;
  clearingNumber?: string;
  accountHolder?: string;
  isDefault?: boolean;
}

// Invoice Information
export interface InvoiceInformation {
  id: string;
  email?: string;
  phoneNumber?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  useCompanyInfo: boolean;
  isDefault: boolean;
}

export interface CreateInvoiceInformationRequest {
  email?: string;
  phoneNumber?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  useCompanyInfo?: boolean;
  isDefault?: boolean;
}

export interface UpdateInvoiceInformationRequest {
  email?: string;
  phoneNumber?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  useCompanyInfo?: boolean;
  isDefault?: boolean;
}

// Delivery Address
export interface DeliveryAddress {
  id: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  isDefault: boolean;
}

export interface CreateDeliveryAddressRequest {
  street: string;
  postalCode: string;
  city: string;
  country?: string;
  isDefault?: boolean;
}

export interface UpdateDeliveryAddressRequest {
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  isDefault?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export class SettingsService {
  // Email Notification Settings
  static async getEmailNotificationSettings(): Promise<EmailNotificationSettings> {
    const response = await api.get<ApiResponse<EmailNotificationSettings>>('/settings/email-notifications');
    return response.data;
  }

  static async updateEmailNotificationSettings(
    settings: Partial<EmailNotificationSettings>
  ): Promise<EmailNotificationSettings> {
    const response = await api.put<ApiResponse<EmailNotificationSettings>>('/settings/email-notifications', settings);
    return response.data;
  }

  // Push Notification Settings (native)
  static async getPushNotificationSettings(): Promise<PushNotificationSettings> {
    const response = await api.get<ApiResponse<PushNotificationSettings>>('/settings/push-notifications');
    return response.data;
  }

  static async updatePushNotificationSettings(
    settings: Partial<PushNotificationSettings>
  ): Promise<PushNotificationSettings> {
    const response = await api.put<ApiResponse<PushNotificationSettings>>('/settings/push-notifications', settings);
    return response.data;
  }

  // Auction Settings
  static async getAuctionSettings(): Promise<AuctionSettings> {
    const response = await api.get<ApiResponse<AuctionSettings>>('/settings/auction');
    return response.data;
  }

  static async updateAuctionSettings(
    settings: Partial<AuctionSettings>
  ): Promise<AuctionSettings> {
    const response = await api.put<ApiResponse<AuctionSettings>>('/settings/auction', settings);
    return response.data;
  }

  // Payment Information
  static async getPaymentInformation(): Promise<PaymentInformation[]> {
    const response = await api.get<ApiResponse<PaymentInformation[]>>('/settings/payment');
    return response.data;
  }

  static async createPaymentInformation(
    data: CreatePaymentInformationRequest
  ): Promise<PaymentInformation> {
    const response = await api.post<ApiResponse<PaymentInformation>>('/settings/payment', data);
    return response.data;
  }

  static async updatePaymentInformation(
    id: string,
    data: UpdatePaymentInformationRequest
  ): Promise<PaymentInformation> {
    const response = await api.put<ApiResponse<PaymentInformation>>(`/settings/payment/${id}`, data);
    return response.data;
  }

  static async setDefaultPaymentInformation(id: string): Promise<PaymentInformation> {
    const response = await api.put<ApiResponse<PaymentInformation>>(`/settings/payment/${id}/default`);
    return response.data;
  }

  static async deletePaymentInformation(id: string): Promise<void> {
    await api.delete(`/settings/payment/${id}`);
  }

  // Invoice Information
  static async getInvoiceInformation(): Promise<InvoiceInformation[]> {
    const response = await api.get<ApiResponse<InvoiceInformation[]>>('/settings/invoice');
    return response.data;
  }

  static async createInvoiceInformation(
    data: CreateInvoiceInformationRequest
  ): Promise<InvoiceInformation> {
    const response = await api.post<ApiResponse<InvoiceInformation>>('/settings/invoice', data);
    return response.data;
  }

  static async updateInvoiceInformation(
    id: string,
    data: UpdateInvoiceInformationRequest
  ): Promise<InvoiceInformation> {
    const response = await api.put<ApiResponse<InvoiceInformation>>(`/settings/invoice/${id}`, data);
    return response.data;
  }

  static async setDefaultInvoiceInformation(id: string): Promise<InvoiceInformation> {
    const response = await api.put<ApiResponse<InvoiceInformation>>(`/settings/invoice/${id}/default`);
    return response.data;
  }

  static async deleteInvoiceInformation(id: string): Promise<void> {
    await api.delete(`/settings/invoice/${id}`);
  }

  // Delivery Addresses
  static async getDeliveryAddresses(): Promise<DeliveryAddress[]> {
    const response = await api.get<ApiResponse<DeliveryAddress[]>>('/settings/delivery');
    return response.data;
  }

  static async createDeliveryAddress(
    data: CreateDeliveryAddressRequest
  ): Promise<DeliveryAddress> {
    const response = await api.post<ApiResponse<DeliveryAddress>>('/settings/delivery', data);
    return response.data;
  }

  static async updateDeliveryAddress(
    id: string,
    data: UpdateDeliveryAddressRequest
  ): Promise<DeliveryAddress> {
    const response = await api.put<ApiResponse<DeliveryAddress>>(`/settings/delivery/${id}`, data);
    return response.data;
  }

  static async setDefaultDeliveryAddress(id: string): Promise<DeliveryAddress> {
    const response = await api.put<ApiResponse<DeliveryAddress>>(`/settings/delivery/${id}/default`);
    return response.data;
  }

  static async deleteDeliveryAddress(id: string): Promise<void> {
    await api.delete(`/settings/delivery/${id}`);
  }
}

